import { get } from 'lodash';
export interface User {
  [key: string]:
    | string
    | number
    | boolean
    | undefined
    | NotificationSettings
    | string[];
  userId: string;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  lastActivity?: string;
  sub: string;
  orgId: string;
  role: number;
  isActive: number;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  email_verified?: boolean;
  notification: NotificationSettings;
  locations: string[];
  groups: string[];
  equipment: string[];
  mfa?: string;
  phoneVerified?: boolean;
  // AER-1725: a real field that was never declared. 11 QA / 9 prod users hold it, all
  // role 5 (DentalEZ Production), and manufacture-process.component.tsx:465 reads it to
  // gate which equipment types they may manufacture. It was mistaken for a stowaway once
  // because Production users are rare; declaring it is the cheapest guard against a
  // future field list being derived from this interface and dropping it.
  eqTypeOptions?: string[];
}

export type NoteAvail = 'email' | 'sms' | 'push';

export interface NotificationSettings {
  [key: string]: NoteAvail[];
  events: NoteAvail[];
  alerts: NoteAvail[];
  service: NoteAvail[];
  reports: NoteAvail[];
  equipmentLeftOn: NoteAvail[];
  equipmentNotConnected: NoteAvail[];
}

export class _User {
  constructor(props?: User) {
    for (let key in props) {
      this[key] = props[key];
    }
    this.name = get(
      props,
      'name',
      `${get(props, 'firstName', '')} ${get(props, 'lastName', '')}`.trim()
    );
    // When props.notification exists, default missing keys to [] rather than a channel list.
    // removeEmpty() strips empty arrays during save, so a missing key means the user
    // explicitly turned it off — defaulting back to ['push'] would silently re-enable it.
    // AER-1545: per-key handling on load.
    // - For pre-existing channels (events/alerts/service/reports) we trust the
    //   stored value when notification exists, defaulting to []. The save flow
    //   writes [null] sentinels for explicitly-cleared keys, which we filter.
    // - For the newer push-only keys (equipmentLeftOn / equipmentNotConnected /
    //   push on alerts), older user records pre-date the keys entirely. The
    //   QA-required default for current users is push=ON, so when the key is
    //   missing we default to ['push']. Once a user has explicitly toggled
    //   off, the save writes [null] (truthy presence) and the filter yields
    //   [] — preserving the OFF intent across reloads.
    //@ts-ignore
    const pickList = (key: string, fallback: NoteAvail[] = []): NoteAvail[] => {
      const raw = get(props, ['notification', key]);
      if (raw === undefined) {
        return fallback;
      }
      // AER-1545: PRESERVE the [null] sentinel through the constructor.
      // Filtering nulls here destroyed the "explicitly off" signal: the
      // reducer wraps every SET_CURRENT_USER payload in `new _User`, so a
      // [null] coming back from the server became [] in redux. Then any
      // subsequent save (notably the per-trackEvent updateUserActivity) ran
      // removeEmpty(), which deletes empty arrays — the alerts key dropped
      // off the record entirely, and on the next read _User defaulted the
      // missing key back to ['push']. The toggle silently re-enabled. Keep
      // [null] in place; the form's isEnabled() check (`.includes('push')`)
      // already treats it as off, and removeEmpty() round-trips it cleanly.
      //@ts-ignore
      return Array.isArray(raw) ? raw : [];
    };

    // AER-1496: Monthly Utilization Reports only ever ships to Org users —
    // checklocations() in server/src/controllers/email/email-scheduler.ts filters
    // on [2, 3, 4].includes(u.role) — and the settings screen now hides the
    // toggle for everyone else (notification-settings.component.tsx REPORT_ROLES).
    // Defaulting 'reports' ON for a DentalEZ or read-only role therefore stored a
    // setting the user could neither see nor ever act on. Role 4 is the fallback
    // so that this class's own default role keeps its existing behaviour; only
    // records that actually carry an ineligible role change.
    const reportsDefault: NoteAvail[] = [2, 3, 4].includes(
      Number(get(props, 'role', 4))
    )
      ? ['email']
      : [];

    const hasNotification = !!(props && (props as User).notification);
    this.notification = (hasNotification
      ? {
          // AER-1545: maintenance push notifications were dropped — always strip
          // any stale 'push' entry from service so it doesn't silently re-trigger.
          events: pickList('events'),
          // alerts: legacy users may not have 'push' in alerts; default to ['push']
          // when missing per QA. If user has explicitly set alerts (key present),
          // honor it.
          alerts:
            get(props, 'notification.alerts') === undefined
              ? (['push'] as NoteAvail[])
              : pickList('alerts'),
          service: pickList('service').filter(s => s !== 'push'),
          reports: pickList('reports'),
          // New push-only keys default to ['push'] when never persisted.
          equipmentLeftOn: pickList('equipmentLeftOn', ['push']),
          equipmentNotConnected: pickList('equipmentNotConnected', ['push']),
        }
      : {
          events: [],
          alerts: ['push'],
          service: [],
          reports: reportsDefault,
          equipmentLeftOn: ['push'],
          equipmentNotConnected: ['push'],
        }) as NotificationSettings;
  }
  [key: string]:
    | string
    | number
    | undefined
    | boolean
    | NotificationSettings
    | string[];
  userId = '';
  createdAt = new Date().toISOString();
  createdBy = undefined;
  updatedAt = new Date().toISOString();
  updatedBy = undefined;
  lastActivity = undefined;
  sub = '';
  orgId = '';
  role = 4;
  isActive = 0;
  name = '';
  firstName = '';
  lastName = '';
  email = '';
  phone = '';
  mfa = '';
  // These field initialisers run before the constructor body, which then always
  // reassigns this.notification — so this object only fixes the shape. The values
  // that actually reach a new user come from the constructor above; in particular
  // AER-1496 gates 'reports' on role there, which cannot be expressed here
  // because a field initialiser has no access to the constructor's props.
  notification: NotificationSettings = {
    events: [], //minor alerts — push OFF by default
    alerts: ['push'], //major alerts
    service: [], //maintenance alerts — push removed entirely
    reports: ['email'], //monthly reports — role-gated in the constructor
    equipmentLeftOn: ['push'],
    equipmentNotConnected: ['push'],
  };
  locations = [];
  groups = [];
  equipment = [];
  phoneVerified = false;
}
